/* The shared paint picker (snippets/paint-picker.liquid): sprayer collections, spray tip collections and /pages/spray-setup.
   Brand row, then a type-ahead combobox (ARIA 1.2: the input owns the listbox and points at the active option) over that
   brand's paints, grouped by what they're for. Data: paint-tips.json (research/spray-selector/build_paint_tips.mjs),
   fetched once, when the picker comes into view or the URL already names a paint.
   A pick is announced on the picker element as CustomEvent 'ase:paint', detail { paint, data } (paint null when cleared),
   and the URL keeps ?paint=<id>. data carries the fan bands (surf), the surface rules (also, always Also suits), the
   Graco guide URL (gg) and the id aliases. 'ase:paint-ready' fires once the list has loaded, detail { api, data }.
   el.paintPicker is the API: { load, select(paint), find(id), paints(), setSurface(surface) }.
   D5 (Jacob, 18 Oct 2026): once a surface is picked (setSurface 'wall', 'door' or 'fence', or ?surface= on load) the list
   leaves out the paints whose own data sheet rules that surface out (paint-tips.json s, 0 for that surface), so picking
   fences hides the interior only paints; a paint whose sheet doesn't say stays listed. A paint already picked stays
   picked, and the page says why it gets no tip there (tip-rank.js suitedTo). */
(() => {
  /* Water based only for now (Jacob, 18 Oct 2026). false shows every paint again. Unknown chemistry is hidden while on. */
  const WATER_BASED_ONLY = true;
  const GROUPS = [
    ['walls', 'Walls and ceilings'],
    ['doors', 'Doors and trim'],
    ['ext', 'Exterior walls'],
    ['primers', 'Primers and undercoats'],
    ['timber', 'Timber and decking'],
    ['other', 'Other'],
  ];
  const EPS = 1e-6;
  const SKEY = { wall: 'w', door: 'd', fence: 'f' };
  const SNAME = { wall: 'walls', door: 'doors and trim', fence: 'fences' };
  const suitsSurface = (p, surface) => !surface || !p.s || p.s[SKEY[surface]] !== 0;
  const $ = (s, r = document) => r.querySelector(s);
  const $$ = (s, r = document) => Array.from(r.querySelectorAll(s));
  const norm = (t) => String(t || '').toLowerCase().normalize('NFKD').replace(/[̀-ͯ]/g, '').replace(/[^a-z0-9]+/g, ' ').trim();
  const fmtO = (x) => Number(x).toFixed(3).replace(/^0/, '');
  const hasRange = (p) => !!p && Array.isArray(p.o) && p.o.length >= 1 && p.o.length <= 2;
  const hasRule = (p) => !!p && !p.na && (hasRange(p) || (Array.isArray(p.tips) && p.tips.length > 0));
  const rangeText = (o) => (o.length === 1 ? `${fmtO(o[0])} and up` : Math.abs(o[0] - o[1]) < EPS ? fmtO(o[0]) : `${fmtO(o[0])} to ${fmtO(o[1])}`);
  /* Largest orifice the paint calls for: top of its range (the floor of an "and up" range), else its biggest listed tip */
  const topOrifice = (p) => {
    if (!hasRule(p)) return NaN;
    if (hasRange(p)) return p.o[p.o.length - 1];
    const os = p.tips.map((c) => Number(String(c).replace(/\D/g, '').slice(-2)) / 1000).filter((x) => x > 0);
    return os.length ? Math.max(...os) : NaN;
  };
  /* A finish at the end of the name moves to its own column: "Wash&Wear Low Sheen" reads Wash&Wear, Low sheen */
  const split = (p) => {
    const f = p.f || '';
    if (f && norm(p.n).endsWith(norm(f)) && norm(p.n) !== norm(f)) {
      const cut = p.n.length - f.length;
      if (p.n.slice(cut).toLowerCase() === f.toLowerCase()) return { name: p.n.slice(0, cut).replace(/[\s,:]+$/, ''), finish: f };
    }
    return { name: p.n, finish: f };
  };

  function init(el) {
    const brandsBox = $('[data-pp-brands]', el);
    const input = $('[data-pp-input]', el);
    const list = $('[data-pp-list]', el);
    const clearBtn = $('[data-pp-clear]', el);
    const basis = $('[data-pp-basis]', el);
    const fine = window.matchMedia('(pointer: fine)').matches;
    let data = { also: {}, surf: { wall: [8, 12], door: [4, 8], fence: [6, 10] }, gg: '', alias: {} };
    let all = null;
    let paints = null;
    let loading = null;
    let brand = '';
    let current = null;
    let options = [];
    let active = -1;
    let surface = SKEY[new URLSearchParams(window.location.search).get('surface')] ? new URLSearchParams(window.location.search).get('surface') : '';

    const emit = () => el.dispatchEvent(new CustomEvent('ase:paint', { bubbles: true, detail: { paint: current, data } }));
    const toUrl = () => {
      const u = new URL(window.location.href);
      if (current) u.searchParams.set('paint', current.id); else u.searchParams.delete('paint');
      history.replaceState(history.state, '', u.pathname + u.search + u.hash);
    };

    function setBrand(b, { focus = false } = {}) {
      brand = b || '';
      $$('[data-pp-brand]', brandsBox).forEach((x) => x.setAttribute('aria-pressed', String(x.dataset.ppBrand === brand)));
      input.disabled = !brand;
      input.placeholder = brand ? `Type a ${brand} paint` : 'Pick the brand first';
      if (current && current.b !== brand) { input.value = ''; choose(null); }
      const on = $('[data-pp-brand][aria-pressed="true"]', brandsBox);
      if (on && brandsBox.scrollWidth > brandsBox.clientWidth) brandsBox.scrollLeft = Math.max(0, on.offsetLeft - brandsBox.offsetLeft - 16);
      if (brand && focus) { input.focus(); fillList(); }
    }

    function load() {
      if (loading) return loading;
      loading = fetch(el.dataset.src, { credentials: 'same-origin' })
        .then((r) => { if (!r.ok) throw new Error(r.status); return r.json(); })
        .then((json) => {
          all = (json && Array.isArray(json.paints) ? json.paints : []).filter((p) => p && p.id && p.b && p.n);
          paints = WATER_BASED_ONLY ? all.filter((p) => p.wb) : all;
          ['also', 'surf', 'gg', 'alias', 'fg', 'enDoor'].forEach((k) => { if (json && json[k]) data[k] = json[k]; });
          const have = new Set(paints.map((p) => p.b));
          $$('[data-pp-brand]', brandsBox).forEach((b) => { if (have.has(b.dataset.ppBrand)) b.disabled = false; else b.remove(); });
          el.dispatchEvent(new CustomEvent('ase:paint-ready', { bubbles: true, detail: { api, data } }));
          return paints;
        })
        .catch(() => {
          input.placeholder = 'Paint list unavailable';
          basis.textContent = "We couldn't load the paint list. Everything is still shown below.";
          all = [];
          paints = [];
          return paints;
        });
      return loading;
    }

    function showBasis(p) {
      basis.textContent = '';
      if (!p) return;
      const range = hasRange(p) ? rangeText(p.o) : '';
      const add = (t) => basis.appendChild(document.createTextNode(t));
      const link = (href, t) => { const a = document.createElement('a'); a.href = href; a.textContent = t; a.rel = 'noopener'; a.target = '_blank'; basis.appendChild(a); };
      const web = (u) => u && /^https?:\/\//.test(u);
      if (p.basis === 'tds' && hasRule(p)) {
        const who = p.note || `${p.b} data sheet${p.pg ? `, page ${p.pg}` : ''}`;
        const named = Array.isArray(p.mk) && p.mk.length ? `, tip ${p.mk.join(' or ')}` : '';
        add(`${who}: ${range ? `${range} orifice${named}` : (p.tips || []).join(', ')}`);
        const what = !p.note ? 'Open the data sheet' : /page$/.test(p.note) ? 'Open the page' : 'Open the guide';
        if (web(p.src)) { add('. '); link(p.src, what); }
      } else if (p.basis === 'field' && hasRule(p)) {
        add(`${p.b} don't print an airless tip on the data sheet. Tips that suit it: ${(p.tips || []).join(', ')}`);
        if (web(p.src)) { add('. '); link(p.src, 'Open the data sheet'); }
      } else if (p.basis === 'graco-guide' && hasRule(p)) {
        add(`No airless range on the ${p.b} sheet. Graco's general guide for ${p.gc || 'this type of coating'}${range ? `: ${range} orifice` : ''}`);
        if (web(data.gg)) { add('. '); link(data.gg, "Open Graco's guide"); }
      } else if (p.basis === 'estimate' && hasRule(p)) {
        /* Peer estimate: labelled as an estimate with its method (FILM-BUILD-MODEL.md section 9) */
        add(`Estimate only. ${p.b} publish no airless tip data, so this range is estimated from similar water based paints${range ? `: ${range} orifice` : ''}${p.w ? `, about ${p.w} µm wet per coat` : ''}. Check the first coat with a wet film gauge. `);
        const a = document.createElement('a');
        a.href = askHref(p);
        a.textContent = 'Ask us';
        basis.appendChild(a);
        add(' to confirm it.');
      } else {
        add(p.na && p.note ? `${p.note} ` : 'No spray settings on file for this paint. ');
        /* D2: our own field note leads where it differs from the sheet (Aquanamel: our team doesn't spray it airless) */
        if (p.gn) add(`Field tested: ${p.gn.replace(/^Our team does not/, "our team doesn't")} `);
        const a = document.createElement('a');
        a.href = askHref(p);
        a.textContent = 'Ask us';
        basis.appendChild(a);
        add(p.na ? ' what to use instead.' : " and we'll check the data sheet.");
      }
    }

    const askHref = (p) => {
      const u = new URL(el.dataset.contact || '/pages/contact', window.location.origin);
      const what = p ? `${p.b} ${p.n}` : `${brand} ${input.value}`.trim();
      u.searchParams.set('part', what ? `Spray setup for ${what}` : 'Spray setup for my paint');
      return u.pathname + u.search;
    };

    function choose(p, { quiet = false } = {}) {
      current = p || null;
      if (current) {
        if (brand !== current.b) { brand = current.b; setBrand(brand); }
        input.value = current.n;
      } else input.value = '';
      clearBtn.hidden = !current;
      closeList();
      showBasis(current);
      toUrl();
      /* Remember the pick for this visit, so the tip finder opened from a product page starts on the same paint */
      try { if (current) sessionStorage.setItem('ase.paint', current.id); else sessionStorage.removeItem('ase.paint'); } catch (err) { /* private mode */ }
      if (!quiet) emit();
    }

    function closeList() {
      list.hidden = true;
      input.setAttribute('aria-expanded', 'false');
      input.removeAttribute('aria-activedescendant');
      active = -1;
    }
    function setActive(i) {
      const items = $$('[role="option"]', list);
      if (!items.length) return;
      active = (i + items.length) % items.length;
      items.forEach((x, k) => x.setAttribute('aria-selected', String(k === active)));
      input.setAttribute('aria-activedescendant', items[active].id);
      items[active].scrollIntoView({ block: 'nearest' });
    }
    function option(id, cls) {
      const o = document.createElement('div');
      o.id = id;
      o.className = cls;
      o.setAttribute('role', 'option');
      o.setAttribute('aria-selected', 'false');
      return o;
    }
    function fillList() {
      if (!paints || !brand) return;
      const words = norm(current && input.value === current.n ? '' : input.value).split(' ').filter(Boolean);
      const named = paints.filter((p) => p.b === brand && words.every((w) => norm(`${p.n} ${p.f || ''}`).includes(w)));
      const mine = named.filter((p) => suitsSurface(p, surface));
      const hidden = named.length - mine.length;
      list.textContent = '';
      options = [];
      let k = 0;
      GROUPS.forEach(([g, label]) => {
        const rows = mine.filter((p) => (p.gp || 'other') === g).sort((x, y) => x.n.localeCompare(y.n));
        if (!rows.length) return;
        const box = document.createElement('div');
        box.setAttribute('role', 'group');
        const h = document.createElement('div');
        h.className = 'tpp__grp';
        h.id = `${list.id}-g-${g}`;
        h.setAttribute('role', 'presentation');
        h.textContent = label;
        box.setAttribute('aria-labelledby', h.id);
        box.appendChild(h);
        rows.forEach((p) => {
          const o = option(`${list.id}-${k}`, 'tpp__opt');
          o.dataset.i = k;
          const s = split(p);
          const n = document.createElement('span');
          n.className = 'tpp__opt-n';
          n.textContent = s.name;
          o.appendChild(n);
          if (s.finish) { const f = document.createElement('span'); f.className = 'tpp__opt-f'; f.textContent = s.finish; o.appendChild(f); }
          if (p.basis === 'tds') {
            const t = document.createElement('span');
            t.className = 'tpp__opt-tds';
            t.textContent = 'Data sheet';
            t.title = "Tip range from the maker's own data sheet";
            o.appendChild(t);
          } else if (p.basis === 'estimate') {
            const t = document.createElement('span');
            t.className = 'tpp__opt-tds';
            t.textContent = 'Estimate';
            t.title = 'Tip range estimated from similar water based paints';
            o.appendChild(t);
          }
          box.appendChild(o);
          options.push(p);
          k += 1;
        });
        list.appendChild(box);
      });
      if (!options.length) {
        const none = document.createElement('p');
        none.className = 'tpp__none';
        none.textContent = hidden ? `No ${brand} paint by that name suits ${SNAME[surface]} on its data sheet.` : `No ${brand} paint by that name on our list yet.`;
        list.appendChild(none);
      }
      if (hidden) {
        const note = document.createElement('p');
        note.className = 'tpp__none';
        note.textContent = `${hidden} ${brand} paint${hidden === 1 ? '' : 's'} left out: not suited to ${SNAME[surface]} on ${hidden === 1 ? 'its' : 'their'} data sheet.`;
        list.appendChild(note);
      }
      const ask = option(`${list.id}-ask`, 'tpp__opt tpp__opt--ask');
      ask.dataset.ask = '';
      ask.textContent = "Can't find your paint? Send us the name";
      list.appendChild(ask);
      list.hidden = false;
      input.setAttribute('aria-expanded', 'true');
      active = -1;
      input.removeAttribute('aria-activedescendant');
    }
    const pickOption = (o) => {
      if (!o) return;
      if (o.hasAttribute('data-ask')) { window.location.href = askHref(null); return; }
      choose(options[Number(o.dataset.i)]);
    };

    /* Brands work straight away; a tap before the list has loaded waits for it */
    $$('[data-pp-brand]', brandsBox).forEach((b) => { b.disabled = false; });
    brandsBox.addEventListener('click', (e) => {
      const b = e.target.closest('[data-pp-brand]');
      if (!b || b.disabled) return;
      const next = b.dataset.ppBrand === brand && !current ? '' : b.dataset.ppBrand;
      load().then(() => {
        if (!b.isConnected) return;
        setBrand(next, { focus: fine });
        if (!fine && brand) fillList();
      });
    });
    input.addEventListener('focus', () => { load().then(() => { if (document.activeElement === input) fillList(); }); });
    input.addEventListener('click', () => { if (list.hidden) load().then(fillList); });
    input.addEventListener('input', () => {
      if (current && input.value !== current.n) { current = null; clearBtn.hidden = true; showBasis(null); toUrl(); try { sessionStorage.removeItem('ase.paint'); } catch (err) { /* private mode */ } emit(); }
      load().then(fillList);
    });
    input.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();
        if (list.hidden) fillList();
        setActive(active + (e.key === 'ArrowDown' ? 1 : -1));
      } else if (e.key === 'Enter') {
        const items = $$('[role="option"]', list);
        if (!list.hidden && active >= 0) { e.preventDefault(); pickOption(items[active]); }
        else if (!list.hidden && options.length === 1) { e.preventDefault(); choose(options[0]); }
      } else if (e.key === 'Escape') {
        if (!list.hidden) { e.preventDefault(); e.stopPropagation(); closeList(); }
        else if (input.value) { e.preventDefault(); input.value = ''; if (current) choose(null); }
      } else if (e.key === 'Tab') {
        closeList();
      }
    });
    input.addEventListener('blur', () => { setTimeout(() => { if (!el.contains(document.activeElement)) closeList(); }, 120); });
    document.addEventListener('click', (e) => { if (!list.hidden && !el.contains(e.target)) closeList(); });
    list.addEventListener('mousedown', (e) => e.preventDefault());
    list.addEventListener('click', (e) => pickOption(e.target.closest('[role="option"]')));
    clearBtn.addEventListener('click', () => { input.value = ''; choose(null); input.focus(); });

    const find = (id) => {
      if (!all || !id) return null;
      const want = (data.alias && data.alias[id]) || id;
      return paints.find((x) => x.id === want) || null;
    };
    const setSurface = (s) => {
      surface = SKEY[s] ? s : '';
      if (!list.hidden) fillList();
    };
    const api = { load, setSurface, surface: () => surface, select: (p) => { load().then(() => choose(p || null)); }, find, paints: () => paints || [], current: () => current, data: () => data, hasRule, hasRange, rangeText, topOrifice, askHref };
    el.paintPicker = api;

    /* ?paint= restores the pick once the list loads; otherwise load when the picker comes into view */
    /* ?paint= wins; on tip pages (inside the paint to tip panel) the paint picked earlier this visit comes back too */
    /* A shared link that names a coating (?coat=) asked for that coating, so the remembered paint stays out of it */
    let remembered = null;
    const named = new URLSearchParams(window.location.search).has('coat');
    try { remembered = el.closest('[data-paint-tips]') && !named ? sessionStorage.getItem('ase.paint') : null; } catch (err) { remembered = null; }
    const wanted = new URLSearchParams(window.location.search).get('paint') || remembered;
    if (wanted) {
      load().then(() => {
        const p = find(wanted);
        if (p) choose(p);
        else {
          basis.textContent = "We couldn't find that paint. Pick it again above.";
          el.dispatchEvent(new CustomEvent('ase:paint', { bubbles: true, detail: { paint: null, data, missing: wanted } }));
        }
      });
    } else {
      if ('IntersectionObserver' in window) {
        const io = new IntersectionObserver((entries) => { if (entries.some((en) => en.isIntersecting)) { io.disconnect(); load(); } }, { rootMargin: '200px' });
        io.observe(el);
      }
      /* and in any case once the page has settled, so the brand row is trimmed to the brands on file */
      const idle = window.requestIdleCallback || ((f) => setTimeout(f, 1500));
      idle(() => load(), { timeout: 3000 });
      el.addEventListener('pointerdown', () => load(), { once: true });
    }
  }

  window.ASE = window.ASE || {};
  window.ASE.paintPicker = { WATER_BASED_ONLY, GROUPS };
  const pickers = Array.from(document.querySelectorAll('[data-paint-picker]'));
  pickers.forEach((el) => { if (!el.paintPicker) init(el); });
  /* On a phone the fixed assistant button (when it is switched on) would sit over the picker's list: tuck it away while
     a picker is on screen (paint-picker.css .is-pt-onscreen) */
  if (pickers.length && document.querySelector('.chat-launch')) {
    const onscreen = () => document.body.classList.toggle('is-pt-onscreen', pickers.some((el) => { const b = el.getBoundingClientRect(); return b.bottom > 0 && b.top < window.innerHeight; }));
    window.addEventListener('scroll', onscreen, { passive: true });
    window.addEventListener('resize', onscreen, { passive: true });
    onscreen();
  }
})();
